import React from 'react';
import PropTypes from 'prop-types';

import CareplanDraft from './careplan/CareplanDraft';

const CareplanResponseView = (props) => {
  const {communicationTarget, isRegenerating, onDiscard, onRegenerate, response} = props;

  const createCareplanDraft = React.useCallback(() => {
    return {
      generalInformation: {
        clientName: response.generalInformation.clientName,
        address: response.generalInformation.address,
        clientInfo: {
          blob: response.generalInformation.clientInfo,
          editorType: 'MARKDOWN',
        },
        notes: {
          blob: response.generalInformation.notes,
          editorType: 'MARKDOWN',
        },
      },
      medicalHistory: {
        medications: {
          blob: response.medicalHistory.medications,
          editorType: 'MARKDOWN',
        },
        conditions: {
          blob: response.medicalHistory.conditions,
          editorType: 'MARKDOWN',
        },
      },
      careRoutine: {
        clientPreferences: {
          blob: response.careRoutine.clientPreferences,
          editorType: 'MARKDOWN',
        },
        dailyTasks: {
          blob: response.careRoutine.dailyTasks,
          editorType: 'MARKDOWN',
        },
      },
      caregiverRequirements: {
        careplanSummary: response.caregiverRequirements.careplanSummary,
        scheduleRequirement: response.caregiverRequirements.scheduleRequirement,
        matchDifficulty: response.caregiverRequirements.matchDifficulty,
        caregiverRequirementChips: response.caregiverRequirements.caregiverRequirementChips,
      },
    };
  }, [response]);
  const [careplanDraft, setCareplanDraft] = React.useState(createCareplanDraft);
  const [saveActionStage, setSaveActionStage] = React.useState('FILL');

  React.useEffect(() => {
    setCareplanDraft(createCareplanDraft());
    setSaveActionStage('FILL');
  }, [createCareplanDraft]);

  React.useEffect(() => {
    if (saveActionStage === 'COMPLETE') {
      onDiscard?.();
    }
  }, [onDiscard, saveActionStage]);

  const handleSave = React.useCallback(() => {
    communicationTarget.updateCareplan(careplanDraft, {
      setStage: setSaveActionStage,
      showProgress: false,
    });
  }, [careplanDraft, communicationTarget]);

  const isSavingCareplan = saveActionStage === 'LOADING';

  const handleSectionChange = React.useCallback((sectionKey, sectionValue) => {
    setCareplanDraft((currentDraft) => ({
      ...currentDraft,
      [sectionKey]: sectionValue,
    }));
  }, []);

  const actions = [
    {
      key: 'save',
      variant: 'primary',
      expression: 'skills.SkillsModule.Careplan.Actions.Save',
      disabled: isSavingCareplan,
      onClick: handleSave,
    },
    {
      key: 'regenerate',
      variant: 'secondary',
      expression: 'skills.SkillsModule.Careplan.Actions.Regenerate',
      disabled: isRegenerating || isSavingCareplan,
      onClick: onRegenerate,
    },
    {
      key: 'discard',
      variant: 'tertiary',
      expression: 'skills.SkillsModule.Careplan.Actions.Discard',
      disabled: isSavingCareplan,
      onClick: onDiscard,
    },
  ];

  return (
    <CareplanDraft
      actions={actions}
      careplanDraft={careplanDraft}
      onSectionChange={handleSectionChange}
    />
  );
};

CareplanResponseView.propTypes = {
  communicationTarget: PropTypes.shape({
    updateCareplan: PropTypes.func.isRequired,
  }).isRequired,
  isRegenerating: PropTypes.bool,
  onDiscard: PropTypes.func,
  onRegenerate: PropTypes.func,
  response: PropTypes.shape({
    careRoutine: PropTypes.shape({
      clientPreferences: PropTypes.string,
      dailyTasks: PropTypes.string,
    }).isRequired,
    caregiverRequirements: PropTypes.shape({
      careplanSummary: PropTypes.string,
      caregiverRequirementChips: PropTypes.arrayOf(PropTypes.string),
      matchDifficulty: PropTypes.string,
      scheduleRequirement: PropTypes.string,
    }).isRequired,
    generalInformation: PropTypes.shape({
      address: PropTypes.string,
      clientInfo: PropTypes.string,
      clientName: PropTypes.string,
      notes: PropTypes.string,
    }).isRequired,
    medicalHistory: PropTypes.shape({
      conditions: PropTypes.string,
      medications: PropTypes.string,
    }).isRequired,
    outputType: PropTypes.string,
  }).isRequired,
};

export default React.memo(CareplanResponseView);
